import { useState } from "react";
import type { ParticipationPromptPublic } from "@tih/shared";
import { ApiError, postParticipationAggregate } from "../../api/client";
import { useEventContent } from "../../context/EventContentContext";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";
import { useBotProtection } from "../../hooks/useBotProtection";
import { EventShareButtons } from "../EventShareButtons";
import { HoneypotField } from "../forms/HoneypotField";
import { TurnstileWidget } from "../forms/TurnstileWidget";
import { hasFeaturedSchedule } from "../../lib/eventDisplay";

type Props = {
  prompt: ParticipationPromptPublic;
  onShared: () => void;
};

export function ParticipationInviteAction({ prompt, onShared }: Props) {
  const { copy } = useSiteCopy();
  const content = useEventContent();
  const hasSchedule = hasFeaturedSchedule(content);
  const [shared, setShared] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function recordShare() {
    if (!ready || busy || shared) return;
    setError(null);
    setBusy(true);
    try {
      await postParticipationAggregate({
        promptSlug: prompt.slug,
        yesNo: true,
        ...botFields,
      });
      setShared(true);
      onShared();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not record");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="participation-invite-action">
      <HoneypotField value={website} onChange={setWebsite} />
      <EventShareButtons
        dateIso={hasSchedule ? content.date : undefined}
        eventTitle={content.eventTitle}
      />
      {!shared ? (
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: 20 }}
          disabled={busy || !ready}
          onClick={() => void recordShare()}
        >
          {String(getAtPath(copy, "home.participation.inviteShared") ?? "I shared an invite")}
        </button>
      ) : (
        <p className="form-success" style={{ marginTop: 20 }}>
          Thank you for spreading the word.
        </p>
      )}
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
